import React, { useEffect, useRef } from "react";
import { InfoBoxProps } from "../types";
import Icon from "@/components/icons";
import { iconTypes, IconTypes } from "@/components/icons/types";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";

export default function LinkedInfoBox({
  icon = "book-open",
  heading,
  headingLink,
  content,
  location,
  position = "1",
}: InfoBoxProps) {
  const webElement: ElementClicked["webElement"] & ElementViewed["webElement"] =
    {
      elementType: ElementType.Link,
      htmlId: "info-box-link",
      location: location || "linked-info-box",
      name: heading ? heading.replace(/\s+/g, "-").toLowerCase() : "info-box",
      text: heading,
      position: position,
    };

  const { elementClicked, elementViewed } = useElementEvents({
    webElement,
  });

  const { inView, ref } = useInView();
  const viewedRef = useRef(false);

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }

    if (!inView) {
      viewedRef.current = false;
    }
  }, [inView, elementViewed]);

  const handleClick = () => {
    elementClicked();
  };

  const isIconType = iconTypes.includes(icon as IconTypes);
  return (
    <div
      ref={ref}
      className="bg-fern-100 lg:p-6 p-8 rounded-xl flex flex-col lg:flex-row container border border-putty-400 items-center"
    >
      <div className="mr-4 lg:block hidden">
        {isIconType && <Icon type={icon as IconTypes} />}
      </div>
      <div className="w-full flex flex-col lg:border-l-[2px] lg:pl-4 border-primary-500">
        <div className="flex-row gap-4">
          {heading && (
            <div className="flex gap-3">
              <div className="block lg:hidden">
                {isIconType && <Icon type={icon as IconTypes} />}
              </div>
              <div>
                <a
                  className="text-primary-500 font-bold !my-0 underline hover:font-normal hover:no-underline text-base hover:text-primary-700"
                  href={headingLink}
                  onClick={handleClick}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {heading}
                </a>
                <p className="mt-4 text-sm !mb-0">{content}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
